import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';

import { FormField, FormFieldType } from '../../../../../../packages/admissions-components/lib';
import { usePageTitle } from '@enroller/utils';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { defaultValues, DocumentsFormValues, documentsSchema } from './DocumentsFormValues.ts';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext.tsx';

export const DocumentsForm = () => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Documents', subtitle: 'Passport' });
    setPageTitle('Documents');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? documentsSchema.required : documentsSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit, trigger } = useForm<DocumentsFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('documents', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          setIsFormLoading(true);
          setTimeout(() => {
            console.log('Form submitted:', data);
            setIsFormLoading(false);
          }, 1000);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setIsFormLoading, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <DocumentSectionWrapper>
        <Grid2 size={12}>
          <Typography variant="subtitle1">Documents</Typography>
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2">Passport</Typography>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="passportDocumentId" name="passportDocument" type={FormFieldType.PictureUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12}>
          <Typography variant="subtitle2">Birth Certificate</Typography>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="birthCertificateId" name="birthCertificateDocument" type={FormFieldType.PictureUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">English Teacher’s Recommendation</Typography>

          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12} container spacing={0}>
          <Grid2 size={12}>
            <FormField
              label=""
              control={control}
              id="englishTeacherRecommendationDocumentId"
              name={`englishTeacherRecommendationDocument`}
              type={FormFieldType.FileUploader}
            />
          </Grid2>

          <Grid2 size={12}>
            <FormField
              control={control}
              name="allowParticipantToView"
              type={FormFieldType.Checkbox}
              label="Allow participant to view English Teacher's Recommendation document"
            />
          </Grid2>
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12}>
          <Typography variant="subtitle2">Transcripts</Typography>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="transcriptsDocumentId" name="transcriptsDocument" type={FormFieldType.PictureUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Transcript Grades Form</Typography>

          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="transcriptGradesFormDocumentId" name={`transcriptGradesFormDocument`} type={FormFieldType.FileUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Medical Records</Typography>

          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="medicalRecordsDocumentId" name={`medicalRecordsDocument`} type={FormFieldType.FileUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Record of Physical Examination</Typography>

          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <FormField
            label=""
            control={control}
            type={FormFieldType.FileUploader}
            id="recordPhysicalExaminationDocumentId"
            name={`recordPhysicalExaminationDocument`}
          />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Dental Certification</Typography>

          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="dentalCertificationDocumentId" name={`dentalCertificationDocument`} type={FormFieldType.FileUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Parent Form</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="parentFormDocumentId" name={`parentFormDocument`} type={FormFieldType.FileUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12}>
          <Typography variant="subtitle2">ELTIS</Typography>
        </Grid2>

        <Grid2 size={12} container spacing={3}>
          <Grid2 size="auto">
            <FormField
              control={control}
              label="ELTIS Test Type"
              type={FormFieldType.Radio}
              itemWrapperProps={{ sx: { pl: 1 } }}
              options={[
                {
                  label: '1.0',
                  value: '1.0',
                },
                {
                  label: '2.0',
                  value: '2.0',
                },
              ]}
              name="eltis.eLTISLevel"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
            <FormField control={control} label="English Test Score" type={FormFieldType.Text} name="eltis.englishTestScore" />
          </Grid2>
        </Grid2>

        <Grid2 size={12} container spacing={1}>
          <Grid2 size={12}>
            <Typography variant="body2">English Test Documentation</Typography>
          </Grid2>
          <Grid2 size={12}>
            <FormField label="" control={control} id="eLTISDocumentId" name="eltis.eLTISDocument" type={FormFieldType.FileUploader} />
          </Grid2>
        </Grid2>
      </DocumentSectionWrapper>
    </Stack>
  );
};

const DocumentSectionWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Grid2 container spacing={3}>
        {children}
      </Grid2>
    </Paper>
  );
};
